import { Sparkles, Zap, Crown } from 'lucide-react';
import { useDarkMode } from '@/hooks/useDarkMode';
import { toast } from 'sonner';

import { Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useDocumentMeta } from '@/hooks/useDocumentMeta';
const PLANS = [
  {
    id: 'free',
    name: 'Free',
    price: '$0',
    cadence: 'forever',
    icon: Sparkles,
    accent: '#9E6752',
    description: 'Everything to keep your gift game strong.',
    features: [
      'Up to 5 keepsakes',
      'AI gift recommendations (limited)',
      '2 collaborative wishlists',
      'Email occasion reminders',
      'Native sharing & deck bundles'
    ],
    cta: 'Current plan',
    isCurrent: true
  },
  {
    id: 'pro',
    name: 'Pro',
    price: '$4.99',
    cadence: 'per month',
    icon: Zap,
    accent: '#FED7A5',
    description: 'For people who give thoughtful gifts year-round.',
    features: [
      'Unlimited keepsakes & wishlists',
      'Unlimited AI recommendations',
      'AI-powered saved searches & alerts',
      'Priority email + in-app reminders',
      'Custom keepsake themes'
    ],
    cta: 'Upgrade to Pro',
    isPopular: true
  },
  {
    id: 'family',
    name: 'Family',
    price: '$9.99',
    cadence: 'per month',
    icon: Crown,
    accent: '#E8B4B8',
    description: 'Share Pro with up to 5 family members.',
    features: [
      'Everything in Pro',
      'Up to 5 family members',
      'Shared family keepsakes',
      'Group gifting & chip-ins',
      'Concierge gift sourcing (early access)'
    ],
    cta: 'Upgrade to Family'
  }
];

export default function Subscription() {
  const darkMode = useDarkMode();

  useDocumentMeta({
    title: 'Pricing, Mementa',
    description:
      'Simple, honest pricing for thoughtful gifting. Start free, upgrade when you outgrow it.',
    url: 'https://mementa.gifts/pricing',
  });

  const handleUpgrade = (planId) => {
    if (planId === 'free') return;
    toast.info('Subscription billing is coming soon, we\'ll email you the moment it\'s live!');
  };

  return (
    <div data-testid="subscription-page" className="min-h-screen transition-colors page-enter" style={{ background: darkMode.bgPrimary }}>
      <div className="max-w-6xl mx-auto px-4 py-12">
        {/* Hero */}
        <div className="text-center mb-12 page-rise-1">
          <div
            className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold mb-4"
            style={{
              background: darkMode.isDark ? 'rgba(254, 215, 165, 0.15)' : 'rgba(158, 103, 82, 0.08)',
              color: darkMode.isDark ? '#FED7A5' : '#9E6752'
            }}
          >
            <Sparkles className="w-3 h-3" />
            Mementa Plans
          </div>
          <h1
            className="text-4xl sm:text-5xl mb-4 transition-colors"
            style={{
              fontFamily: 'Playfair Display, serif',
              color: darkMode.textPrimary,
              fontWeight: '700'
            }}
          >
            Pick a plan that grows with your gifting
          </h1>
          <p className="text-base max-w-2xl mx-auto" style={{ color: darkMode.textSecondary }}>
            Start free. Upgrade when you need unlimited keepsakes, AI alerts, or to share with family. No surprises, cancel any time.
          </p>
        </div>

        {/* Plan grid */}
        <div className="grid md:grid-cols-3 gap-5">
          {PLANS.map((plan, planIdx) => {
            const Icon = plan.icon;
            const isFeatured = plan.isPopular;
            return (
              <div
                key={plan.id}
                data-testid={`plan-card-${plan.id}`}
                className={`relative p-6 rounded-2xl transition-all hover:scale-[1.02] page-rise-${planIdx + 2}`}
                style={{
                  background: darkMode.bgSecondary,
                  border: `2px solid ${isFeatured ? plan.accent : darkMode.borderPrimary}`,
                  boxShadow: isFeatured ? '0 8px 32px rgba(254, 215, 165, 0.25)' : '0 2px 8px rgba(0,0,0,0.04)'
                }}
              >
                {isFeatured && (
                  <div
                    className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full text-[11px] font-bold uppercase tracking-wider"
                    style={{
                      background: plan.accent,
                      color: '#20212B'
                    }}
                  >
                    Most popular
                  </div>
                )}
                <div className="flex items-center gap-3 mb-3">
                  <div
                    className="w-10 h-10 rounded-xl flex items-center justify-center"
                    style={{
                      background: `${plan.accent}25`,
                      color: plan.accent
                    }}
                  >
                    <Icon className="w-5 h-5" />
                  </div>
                  <h2
                    className="text-2xl font-semibold transition-colors"
                    style={{ fontFamily: 'Playfair Display, serif', color: darkMode.textPrimary }}
                  >
                    {plan.name}
                  </h2>
                </div>

                <div className="flex items-baseline gap-1 mb-2">
                  <span className="text-4xl font-bold" style={{ color: darkMode.textPrimary, fontFamily: 'Playfair Display, serif' }}>
                    {plan.price}
                  </span>
                  <span className="text-xs" style={{ color: darkMode.textSecondary }}>/{plan.cadence}</span>
                </div>
                <p className="text-sm mb-5" style={{ color: darkMode.textSecondary }}>
                  {plan.description}
                </p>

                <ul className="space-y-2 mb-6">
                  {plan.features.map((feature) => (
                    <li key={feature} className="flex items-start gap-2 text-sm" style={{ color: darkMode.textPrimary }}>
                      <Check className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: plan.accent }} />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>

                <Button
                  data-testid={`plan-cta-${plan.id}`}
                  onClick={() => handleUpgrade(plan.id)}
                  disabled={plan.isCurrent}
                  className="w-full transition-all"
                  style={{
                    background: plan.isCurrent ? 'transparent' : plan.accent,
                    color: plan.isCurrent ? darkMode.textSecondary : '#20212B',
                    border: plan.isCurrent ? `2px solid ${darkMode.borderPrimary}` : 'none',
                    fontWeight: '600',
                    borderRadius: '12px',
                    padding: '12px'
                  }}
                >
                  {plan.cta}
                </Button>
              </div>
            );
          })}
        </div>

        {/* FAQ-ish footer */}
        <div className="mt-12 text-center">
          <p className="text-xs" style={{ color: darkMode.textSecondary }}>
            Billing is processed by our payment partner. Prices in USD; taxes calculated at checkout. Cancel any time from your profile.
          </p>
          <p className="text-xs mt-2" style={{ color: darkMode.textMuted }}>
            Currently in early-access, billing will turn on for new sign-ups within the next few weeks.
          </p>
        </div>
      </div>
    </div>
  );
}
